/**
 * theme/spacing.ts
 * Spacing, layout geometry and control heights, taken from the design source.
 * Screens never write a raw pixel gap — they name one of these.
 */

/** Generic spacing scale. */
export const space = {
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  xxl: 14,
  x3: 15,
  x4: 16,
  x5: 18,
  x6: 20,
  x7: 24,
  x8: 26,
  x9: 32,
  x10: 44,
  x11: 52,
} as const;

/**
 * Max content width per screen. The design centres different screens at
 * different widths on purpose — a dense register needs more room than an
 * approver queue, and onboarding is deliberately narrow.
 */
export const contentWidth = {
  default: 1240,
  register: 1400,
  settings: 1040,
  settlement: 1040,
  approver: 900,
  onboarding: 720,
} as const;

/** Page container padding — `26px 32px 52px` in the design. */
export const pagePadding = { top: 26, horizontal: 32, bottom: 52 } as const;
/** Onboarding container padding — `44px 32px 60px`. */
export const onboardingPadding = { top: 44, horizontal: 32, bottom: 60 } as const;

/** Fixed chrome geometry. */
export const chrome = {
  headerHeight: 52,
  headerPaddingX: 18,
  sidebarWidth: 214,
  sidebarCollapsedWidth: 56,
  sidebarPaddingY: 14,
  sidebarPaddingX: 10,
  drawerWidth: 520,
  /** The drawer never covers the whole viewport — 92vw on a narrow window. */
  drawerMaxWidthRatio: 0.92,
  modalWidth: 520,
  modalMaxHeightRatio: 0.88,
} as const;

/** Control heights. Every button/input in the app is one of these. */
export const controlHeight = {
  /** Card-header buttons. */
  xs: 27,
  /** Compact buttons, chips. */
  sm: 29,
  /** Header primary, table-row buttons. */
  md: 30,
  /** Modal footer buttons. */
  mdPlus: 31,
  /** Inputs and selects. */
  input: 33,
  /** Drawer action buttons. */
  lg: 34,
  /** Custodian "record a transaction" tiles. */
  xl: 38,
} as const;

/** Card interior geometry. */
export const card = {
  padding: 15,
  paddingLg: 18,
  headerPaddingY: 12,
  headerPaddingX: 15,
  rowPaddingY: 11,
  rowPaddingYLg: 13,
  /** 15px in the design source (the README's 16 is the column gap). */
  rowPaddingX: 15,
  /** Gap between cards in a grid. */
  gridGap: 14,
  /** Gap between KPI cards. */
  kpiGap: 12,
  /** Gap between table columns. */
  columnGap: 16,
  /** Accent strip on a KPI / balance card. */
  accentStripHeight: 3,
} as const;

/** Minimum track widths for the `auto-fit minmax()` grids in the design. */
export const gridMin = {
  kpi: 210,
  chart: 330,
  custodian: 300,
  reconLeg: 150,
} as const;

export const spacing = {
  space,
  contentWidth,
  pagePadding,
  onboardingPadding,
  chrome,
  controlHeight,
  card,
  gridMin,
} as const;
